import { useId, useState } from "react";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import {
  createNews,
  deleteNews,
  getAdminNews,
  NEWS_CATEGORIES,
  updateNews,
} from "../../api/news";
import { ArticleContent } from "../News/NewsComponents";
import { NewsBodyEditor } from "./NewsBodyEditor";
import { useAdminData, useAdminQuery, useAdminTask } from "./useAdminData";
import { dateTime } from "./adminModel";
import {
  ActionMenu,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  Field,
  FilterSelect,
  Notice,
  Pagination,
  ResourceContent,
  SearchField,
  SectionHeader,
  Toolbar,
} from "./components/AdminUI";
import styles from "./admin.module.css";

const loadNews = ({ q, status, category, ...page }) =>
  getAdminNews({
    ...page,
    query: q.slice(0, 180),
    category,
    is_published: status ? status === "published" : undefined,
  });
const formFromArticle = (article) => ({
  title: article.title || "",
  excerpt: article.excerpt || "",
  body: article.body || "",
  category: article.category || "События",
  cover_url: article.cover_url || "",
});

export function NewsEditor({
  article,
  onClose,
  onSaved,
  startPreview = false,
}) {
  const formId = useId();
  const [form, setForm] = useState(() => formFromArticle(article));
  const [preview, setPreview] = useState(startPreview);
  const task = useAdminTask();
  const dirty =
    JSON.stringify(form) !== JSON.stringify(formFromArticle(article));
  const update = (key) => (event) =>
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
  const save = (event) => {
    event.preventDefault();
    const isPublished =
      article.is_published || event.nativeEvent.submitter?.value === "publish";
    const payload = {
      ...Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, value.trim()]),
      ),
      is_published: Boolean(isPublished),
    };
    payload.cover_url = payload.cover_url || null;
    if (
      payload.title.length < 3 ||
      payload.excerpt.length < 10 ||
      payload.body.length < 20
    ) {
      task.setError(
        "Проверьте текст: заголовок — от 3 символов, описание — от 10, текст — от 20.",
      );
      setPreview(false);
      return;
    }
    task.run(
      () =>
        article.id ? updateNews(article.id, payload) : createNews(payload),
      () => {
        onSaved(
          isPublished
            ? "Новость сохранена и доступна на сайте."
            : "Черновик сохранён. Он виден только в админке.",
        );
        onClose();
      },
    );
  };
  return (
    <Dialog
      title={article.id ? "Редактирование новости" : "Новая новость"}
      description="Напишите историю, проверьте, как она выглядит, и поделитесь с участниками."
      onClose={onClose}
      busy={task.busy}
      dirty={dirty}
      footer={(requestClose) => (
        <>
          <Notice danger>{task.error}</Notice>
          <div className={styles.footerButtons}>
            <Button onClick={requestClose} disabled={task.busy}>
              Отмена
            </Button>
            {!preview && (
              <>
                {!article.is_published && (
                  <Button
                    type="submit"
                    form={formId}
                    value="draft"
                    disabled={task.busy}
                  >
                    Сохранить черновик
                  </Button>
                )}
                <Button
                  type="submit"
                  form={formId}
                  value="publish"
                  variant="primary"
                  disabled={task.busy}
                >
                  {task.busy
                    ? "Сохраняем…"
                    : article.is_published
                      ? "Сохранить изменения"
                      : "Опубликовать"}
                </Button>
              </>
            )}
            {preview && (
              <Button variant="primary" onClick={() => setPreview(false)}>
                К редактированию
              </Button>
            )}
          </div>
        </>
      )}
    >
      <div className={styles.newsEditorTabs}>
        <div>
          <Button
            variant={!preview ? "primary" : "secondary"}
            aria-pressed={!preview}
            onClick={() => setPreview(false)}
            icon="edit"
          >
            Редактор
          </Button>
          <Button
            variant={preview ? "primary" : "secondary"}
            aria-pressed={preview}
            onClick={() => setPreview(true)}
            icon="globe"
          >
            Предпросмотр
          </Button>
        </div>
        <Badge tone={article.is_published ? "success" : "neutral"}>
          {article.is_published ? "На сайте" : "Черновик"}
        </Badge>
      </div>
      {preview ? (
        <div className={styles.newsPreview}>
          <p className={styles.hint}>
            Так материал будет выглядеть на странице новости. Изменения пока не
            сохранены.
          </p>
          <ArticleContent article={{ ...article, ...form }} preview />
        </div>
      ) : (
        <form id={formId} onSubmit={save} className={styles.stack}>
          <Field
            label="Заголовок"
            value={form.title}
            onChange={update("title")}
            required
            minLength={3}
            maxLength={180}
            placeholder="О чём эта новость?"
            autoFocus
          />
          <Field
            label="Краткое описание"
            type="textarea"
            value={form.excerpt}
            onChange={update("excerpt")}
            required
            minLength={10}
            maxLength={320}
            rows={3}
            hint={`${form.excerpt.length}/320 · Покажем в карточке и под заголовком.`}
            placeholder="Главное в одном-двух предложениях"
          />
          <Field
            label="Категория"
            value={form.category}
            onChange={update("category")}
          >
            {NEWS_CATEGORIES.map((category) => (
              <option key={category}>{category}</option>
            ))}
          </Field>
          <Field
            label="Ссылка на обложку"
            type="url"
            value={form.cover_url}
            onChange={update("cover_url")}
            maxLength={2048}
            placeholder="https://…/image.jpg"
            hint="Необязательно. Без картинки новость оформим как текстовую публикацию — с акцентом на заголовке и анонсе."
          />
          <NewsBodyEditor
            value={form.body}
            onChange={(body) => setForm((previous) => ({ ...previous, body }))}
            disabled={task.busy}
          />
          <p className={styles.hint}>
            {article.is_published
              ? "Сохранённые изменения сразу появятся на сайте. Дата первой публикации останется прежней."
              : "Черновик виден только администраторам. После публикации новость появится в ленте и на главной."}
          </p>
        </form>
      )}
    </Dialog>
  );
}

export function NewsSection({ notify, initialAction, clearAction }) {
  const query = useAdminQuery({ status: "", category: "" });
  const resource = useAdminData(loadNews, query.values);
  const [editor, setEditor] = useState(
    initialAction === "create" ? { article: {} } : null,
  );
  const [confirm, setConfirm] = useState(null);
  const changed = (message) => {
    notify(message);
    resource.refresh();
  };
  const closeEditor = () => {
    setEditor(null);
    clearAction();
  };
  return (
    <>
      <SectionHeader
        title="Новости"
        description="Истории команд, события и объявления для сайта и главной страницы."
      >
        <Link
          to="/news"
          target="_blank"
          rel="noreferrer"
          className={styles.siteLink}
        >
          Открыть ленту ↗
        </Link>
        <Button
          variant="primary"
          icon="plus"
          onClick={() => setEditor({ article: {} })}
        >
          Создать новость
        </Button>
      </SectionHeader>
      <div className={styles.panel}>
        <Toolbar
          onRefresh={resource.refresh}
          loading={resource.loading}
          onReset={query.reset}
          filtered={
            query.values.q || query.values.status || query.values.category
          }
        >
          <SearchField
            value={query.values.q}
            onChange={(q) => query.update({ q })}
            placeholder="Заголовок или описание"
          />
          <FilterSelect
            label="Публикация"
            value={query.values.status}
            onChange={(status) => query.update({ status })}
            options={[
              { value: "", label: "Все статусы" },
              { value: "published", label: "Опубликованы" },
              { value: "draft", label: "Черновики" },
            ]}
          />
          <FilterSelect
            label="Категория"
            value={query.values.category}
            onChange={(category) => query.update({ category })}
            options={[
              { value: "", label: "Все категории" },
              ...NEWS_CATEGORIES.map((value) => ({ value, label: value })),
            ]}
          />
        </Toolbar>
        <ResourceContent
          resource={resource}
          emptyTitle="Новостей пока нет"
          emptyDescription="Создайте первую публикацию или измените фильтры поиска."
          emptyAction={
            <Button icon="plus" onClick={() => setEditor({ article: {} })}>
              Создать новость
            </Button>
          }
        >
          <DataTable
            caption="Новости сайта"
            rowKey="id"
            items={resource.items}
            columns={[
              {
                key: "title",
                label: "Новость",
                render: (article) => (
                  <div className={styles.cellStack}>
                    <button
                    className={`${styles.cellTitle} ${styles.cellTitleButton}`}
                      onClick={() => setEditor({ article })}
                    >
                      {article.title}
                    </button>
                    <span className={styles.cellDescription}>
                      {article.excerpt}
                    </span>
                  </div>
                ),
              },
              {
                key: "category",
                label: "Категория",
                render: (article) => <Badge>{article.category}</Badge>,
              },
              {
                key: "status",
                label: "Статус",
                render: (article) => (
                  <Badge tone={article.is_published ? "success" : "neutral"}>
                    {article.is_published ? "Опубликована" : "Черновик"}
                  </Badge>
                ),
              },
              {
                key: "date",
                label: "Обновлена",
                render: (article) => (
                  <span className={styles.cellMeta}>
                    {dateTime(article.updated_at)}
                  </span>
                ),
              },
              {
                key: "actions",
                label: "Действия",
                render: (article) => (
                  <div className={styles.rowActions}>
                    <Button icon="edit" onClick={() => setEditor({ article })}>
                      Изменить
                    </Button>
                    <ActionMenu
                      label={article.title}
                      actions={[
                        {
                          label: "Предпросмотр",
                          icon: "globe",
                          onClick: () => setEditor({ article, preview: true }),
                        },
                        article.is_published && {
                          label: "Открыть на сайте",
                          icon: "external",
                          onClick: () =>
                            window.open(
                              `/news/${article.id}`,
                              "_blank",
                              "noopener,noreferrer",
                            ),
                        },
                        {
                          label: article.is_published
                            ? "Снять с публикации"
                            : "Опубликовать",
                          icon: "check",
                          onClick: () =>
                            setConfirm({ article, type: "publish" }),
                        },
                        {
                          label: "Удалить",
                          icon: "close",
                          danger: true,
                          onClick: () =>
                            setConfirm({ article, type: "delete" }),
                        },
                      ]}
                    />
                  </div>
                ),
              },
            ]}
          />
          <Pagination query={query} resource={resource} />
        </ResourceContent>
      </div>
      {editor && (
        <NewsEditor
          article={editor.article}
          startPreview={editor.preview}
          onClose={closeEditor}
          onSaved={changed}
        />
      )}
      {confirm && (
        <ConfirmDialog
          title={
            confirm.type === "delete"
              ? "Удалить новость?"
              : confirm.article.is_published
                ? "Снять новость с публикации?"
                : "Опубликовать новость?"
          }
          description={
            confirm.type === "delete"
              ? `«${confirm.article.title}» будет удалена без возможности восстановления.`
              : confirm.article.is_published
                ? "Новость исчезнет из ленты и с главной. Вы сможете опубликовать её снова."
                : "Новость станет доступна всем посетителям сайта."
          }
          danger={confirm.type === "delete"}
          confirmLabel={
            confirm.type === "delete"
              ? "Удалить новость"
              : confirm.article.is_published
                ? "Снять с публикации"
                : "Опубликовать"
          }
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            if (confirm.type === "delete") {
              await deleteNews(confirm.article.id);
              changed("Новость удалена.");
            } else {
              await updateNews(confirm.article.id, {
                is_published: !confirm.article.is_published,
              });
              changed(
                confirm.article.is_published
                  ? "Новость возвращена в черновики."
                  : "Новость опубликована.",
              );
            }
          }}
        />
      )}
    </>
  );
}
